import {
  put, call, takeLatest, select
} from 'redux-saga/effects';
import { Notification as customNotification } from '../../components/ui/custom-notification';
import { get } from 'lodash';
import {
  FETCH_USERS,
  ADD_USER,
  UPDATE_USER,
  DELETE_USER,
  BULK_ACTION_USERS,
  RESET_USER_PASSWORD,
  TOGGLE_TWO_FACTOR,
  GET_USER_ACCESS,
  SAVE_USER_ACCESS,
  RESTORE_USER_ACCESS,
  GET_USER_ACCESS_PERMISSIONS
} from '../types';
import {
  setUsers,
  setUsersFailure,
  addUserSuccess,
  addUserFailure,
  updateUserSuccess,
  updateUserFailure,
  deleteUserSuccess,
  deleteUserFailure,
  bulkActionUsersSuccess,
  bulkActionUsersFailure,
  resetUserPasswordSuccess,
  resetUserPasswordFailure,
  toggleUserTwoFactorSuccess,
  toggleUserTwoFactorFailure,
  // User access permissions
  setUserAccess,
  setUserAccessFailure,
  updateUserAccessSuccess,
  updateUserAccessFailure,
  saveUserAccessSuccess,
  saveUserAccessFailure,
  restoreUserAccessSuccess,
  restoreUserAccessFailure,
  // User permissions after login
  setUserAccessPermissions,
  setUserAccessPermissionsFailure
} from './actions';
import axiosConfig from './axiosConfig';
import { parseUserExtraDetails } from '../../features/admin/utils/parseUserExtraDetails';
import userAccessApi from '../../features/admin/services/userAccessApi';

// API functions
const api = {
  fetchUsers: async (filters) => {

    let url = `/user/getallusers`;
    if (filters && Object.keys(filters).length > 0) {
      const queryParams = new URLSearchParams(filters).toString();
      url += `?${queryParams}`;
    }

    try {
      const res = await axiosConfig.get(url);
  

      if (res.status === 403) {
        customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
        return null;
      }
      if (res.status === 200) {
        const body = get(res, 'data', {});
        // Support: { data: User[] }, { data: { data: User[] } }, raw User[], or { users: User[] }
        const list = Array.isArray(body)
          ? body
          : Array.isArray(body.data)
            ? body.data
            : Array.isArray(body.data?.data)
              ? body.data.data
              : Array.isArray(body.users)
                ? body.users
                : [];

        // Transform API response to match frontend expected format
        const transformedUsers = list
          .filter((user) => user != null && (user.user_id != null || user.id != null))
          .map((user) => {
            const cognito = user.cognitoStatus;
            const isConfirmed = cognito?.status === 'CONFIRMED';

            // Map role names from API to frontend format
            let role = 'user'; // default
            if (user.appLevelRole?.group_name) {
              const groupName = user.appLevelRole.group_name.toLowerCase();
              if (groupName === 'super admin') role = 'super_admin';
              else if (groupName === 'admin') role = 'admin';
              else if (groupName === 'manager') role = 'manager';
              else if (groupName === 'viewer') role = 'viewer';
              else role = groupName;
            }

            const userId = user.user_id != null ? user.user_id : user.id;

            return {
              id: userId,
              user_id: user.user_id != null ? user.user_id : userId,
              name: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email || String(userId),
              lastName: user.last_name,
              firstName: user.first_name,
              email: user.email,
              phone: user.phone_number,
              status:
                user.status === 'inactive'
                  ? user.status
                  : isConfirmed
                    ? 'active'
                    : user.status,
              role,
              cognitoUserId: user.cognito_user_id,
              createdAt: user.created_at,
              lastLogin: cognito?.userLastModifiedDate || user.created_at,
              twoFactorEnabled: user.two_factor_enabled || false,
              requirePasswordChange: isConfirmed ? false : user.status === 'pending' || false,
              profile: {
                department: user.department
                  ? {
                      id: user.department.department_id,
                      name: user.department.department_name,
                      isActive: user.department.is_active
                    }
                  : null,
                company: user.company
                  ? {
                      id: user.company.company_id,
                      name: user.company.company_name,
                      isActive: user.company.is_active
                    }
                  : null
              },
              permissions: user.appLevelRole?.permissions || {},
              user_extra_details: parseUserExtraDetails(
                user.user_extra_details ?? user.userExtraDetails
              ),
            };
          });

        return transformedUsers;
      }
      return [];
    } catch (error) {
      throw error;
    }
  },

  addUser: async (userData) => {
    try {
      const res = await axiosConfig.post(`/user/createUser`, userData);

      if (res.status === 403) {
        customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
        return null;
      }
      if (res.status === 200) {
        return res.data;
      }
      if (res.status === 502) {
        customNotification.error('Service temporarily unavailable', 'The service is temporarily unavailable. Please try again later.');
        return null;
      }
      return null;
    } catch (error) {
      customNotification.error('Failed to add user', error.message);
      return null;
    }
  },

  updateUser: async (userData) => {
    try {
      const res = await axiosConfig.put(`/user/update/${userData.id}`, userData);

      if (res.status === 403) {
        customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
        return null;
      }
      if (res.status === 200) {
        return res.data;
      }
      return null;
    } catch (error) {
      customNotification.error('Failed to update user', error.message);
      return null;
    }
  },

  deleteUser: async (userId) => {
    try {
      const data = {
        userIds: [userId]
      }
      const res = await axiosConfig.delete(`/user/deleteUsers`, {
        data: data
      });

      if (res.status === 403) {
        customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
        return null;
      }
      if (res.status === 200) {
        return userId;
      }
      return null;
    } catch (error) {
      customNotification.error('Failed to delete user', error.message);
      return null;
    }
  },

  bulkActionUsers: async (bulkAction) => {
    try {
      const res = await axiosConfig.post(`/user/bulk-action`, bulkAction);

      if (res.status === 403) {
        customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
        return null;
      }
      if (res.status === 200) {
        return res.data;
      }
      return null;
    } catch (error) {
      customNotification.error('Failed to perform bulk action', error.message);
      return null;
    }
  },

  // resetPassword: async (email, ttlSeconds, platform) => {
  //   try {
  //     const res = await axiosConfig.post(`/auth/invite`, { email, ttlSeconds, platform });

  //     if (res.status === 403) {
  //       customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
  //       return null;
  //     }
  //     if (res.status === 200) {
  //       customNotification.success('Password reset successfully', 'The user password has been reset.');
  //       return userId;
  //     }
  //     return null;
  //   } catch (error) {
  //     customNotification.error('Failed to reset password', error.message);
  //     return null;
  //   }
  // },

  resetPassword: async (email, ttlSeconds, platform) => {
   
    const platform1 = "Data360";
  try {
    const res = await fetch(`${process.env.REACT_APP_EMAIL_NOTIFICATION_BASE_URL}/auth/invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, ttlSeconds, platform1 }),
    });
   // console.log("Response from resetPassword API:", res);

    if (res.status === 403) {
      customNotification.error(
        'Unauthorized',
        'You do not have permission to access this resource.'
      );
      return null;
    }

    if (res.status === 200) {
      customNotification.success(
        'Invite Sent',
        'The invitation link has been successfully sent to the user.'
      );
      return true;
    }

    const errorText = await res.text();
    customNotification.error('Failed to send invite', errorText || 'Unknown error');
    return null;

  } catch (error) {
    customNotification.error('Failed to send invite', error.message);
    return null;
  }
},


  toggleTwoFactor: async (userId, enabled) => {
    try {
      const res = await axiosConfig.post(`/user/toggle-two-factor/${userId}`, { enabled });

      if (res.status === 403) {
        customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
        return null;
      }
      if (res.status === 200) {
        return { userId, twoFactorEnabled: enabled };
      }
      return null;
    } catch (error) {
      customNotification.error('Failed to toggle two factor authentication', error.message);
      return null;
    }
  },

  // User Access Permissions API functions
  getUserAccess: async () => {
    try {
      const res = await axiosConfig.get('/user/permissions/access');

      if (res.status === 403) {
        customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
        return null;
      }
      if (res.status === 200) {
        return res.data.data || {};
      }
      return {};
    } catch (error) {
      customNotification.error('Failed to fetch user access permissions', error.message);
      return null;
    }
  },

  saveUserAccess: async (rolePermissions, updatedBy) => {
    try {
      const permissionsData = {
        rolePermissions,
        updatedBy,
        updatedAt: new Date().toISOString()
      };

      const res = await axiosConfig.post('/user/permissions/access', permissionsData);

      if (res.status === 403) {
        customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
        return null;
      }
      if (res.status === 200) {
        customNotification.success('Permissions saved successfully', 'User access permissions have been saved to the database.');
        return res.data;
      }
      return null;
    } catch (error) {
      customNotification.error('Failed to save permissions', error.message);
      return null;
    }
  },

  restoreUserAccess: async () => {
    try {
      const res = await axiosConfig.get('/user/permissions/access/restore');

      if (res.status === 403) {
        customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
        return null;
      }
      if (res.status === 200) {
        customNotification.success('Permissions restored successfully', 'User access permissions have been restored from the database.');
        return res.data.data || {};
      }
      return null;
    } catch (error) {
      customNotification.error('Failed to restore permissions', error.message);
      return null;
    }
  }
};

// Sagas
function* fetchUsersSaga(action) {
  try {
    const users = yield call(api.fetchUsers, action.filters);
    if (users !== null) {
      yield put(setUsers({ usersData: users }));
    } else {
      yield put(setUsersFailure('Failed to fetch users'));
    }
  } catch (error) {
    yield put(setUsersFailure(error.message));
  }
}

function* addUserSaga(action) {
  try {
    const newUser = yield call(api.addUser, action.userData);
    if (newUser !== null) {
      yield put(addUserSuccess(newUser));
      customNotification.success('User added successfully', 'The user has been added successfully.');
    } else {
      yield put(addUserFailure('Failed to add user'));
    }
  } catch (error) {
    yield put(addUserFailure(error.message));
  }
}

function* updateUserSaga(action) {
  try {
    const updatedUser = yield call(api.updateUser, action.userData);
    if (updatedUser !== null) {
      yield put(updateUserSuccess(updatedUser));
      customNotification.success('User updated successfully', 'The user has been updated successfully.');
    } else {
      yield put(updateUserFailure('Failed to update user'));
    }
  } catch (error) {
    yield put(updateUserFailure(error.message));
  }
}

function* deleteUserSaga(action) {
  try {
    const userId = yield call(api.deleteUser, action.userId);
    if (userId !== null) {
      yield put(deleteUserSuccess(userId));
      customNotification.success('User deleted successfully', 'The user has been deleted successfully.');
    } else {
      yield put(deleteUserFailure('Failed to delete user'));
    }
  } catch (error) {
    yield put(deleteUserFailure(error.message));
  }
}

function* bulkActionUsersSaga(action) {
  try {
    const result = yield call(api.bulkActionUsers, action.bulkAction);
    if (result !== null) {
      yield put(bulkActionUsersSuccess(result));
      customNotification.success('Bulk action completed successfully', 'The bulk action has been completed successfully.');
    } else {
      yield put(bulkActionUsersFailure('Failed to perform bulk action'));
    }
  } catch (error) {
    yield put(bulkActionUsersFailure(error.message));
  }
}

function* resetPasswordSaga(action) {
  try {
    const email = yield call(api.resetPassword, action.email, action.ttlSeconds, action.platform);
    if (email !== null) {
      yield put(resetUserPasswordSuccess(email));
    } else {
      yield put(resetUserPasswordFailure('Failed to reset password'));
    }
  } catch (error) {
    yield put(resetUserPasswordFailure(error.message));
  }
}

function* toggleTwoFactorSaga(action) {
  try {
    const currentUser = yield select(state => state.userManagement.users.find(u => u.id === action.userId));
    const enabled = !currentUser.twoFactorEnabled;
    const result = yield call(api.toggleTwoFactor, action.userId, enabled);
    if (result !== null) {
      yield put(toggleUserTwoFactorSuccess(result.userId, result.twoFactorEnabled));
      customNotification.success(`Two factor authentication ${enabled ? 'enabled' : 'disabled'} successfully`, 'The two factor authentication has been toggled successfully.');
    } else {
      yield put(toggleUserTwoFactorFailure('Failed to toggle two factor authentication'));
    }
  } catch (error) {
    yield put(toggleUserTwoFactorFailure(error.message));
  }
}

// User Access Permissions Sagas
function* getUserAccessSaga() {
  try {
    const result = yield call(userAccessApi.getUserAccess);
    if (result.success) {
      yield put(setUserAccess(result.data));
    } else {
      yield put(setUserAccessFailure(result.error || 'Failed to fetch user access permissions'));
    }
  } catch (error) {
    yield put(setUserAccessFailure(error.message));
  }
}

function* saveUserAccessSaga(action) {
  try {
    const result = yield call(userAccessApi.saveUserAccess, action.rolePermissions, action.updatedBy);
    if (result.success) {
      yield put(saveUserAccessSuccess());
    } else {
      yield put(saveUserAccessFailure(result.error || 'Failed to save user access permissions'));
    }
  } catch (error) {
    yield put(saveUserAccessFailure(error.message));
  }
}

function* restoreUserAccessSaga() {
  try {
    const result = yield call(userAccessApi.restoreUserAccess);
    if (result.success) {
      yield put(restoreUserAccessSuccess(result.data));
    } else {
      yield put(restoreUserAccessFailure(result.error || 'Failed to restore user access permissions'));
    }
  } catch (error) {
    yield put(restoreUserAccessFailure(error.message));
  }
}
function* getUserAccessPermissionsSaga(action) {
  try {
    const { cognitoId } = action;
    const result = yield call(userAccessApi.getUserAccessPermissions, { cognitoId });
    if (result.success) {
      yield put(setUserAccessPermissions(result.data));
    } else {
      yield put(setUserAccessPermissionsFailure(result.error || 'Failed to get user access permissions'));
    }
  } catch (error) {
    yield put(setUserAccessPermissionsFailure(error.message));
  }
}

// Root saga
export function* userSaga() {
  yield takeLatest(FETCH_USERS, fetchUsersSaga);
  yield takeLatest(ADD_USER, addUserSaga);
  yield takeLatest(UPDATE_USER, updateUserSaga);
  yield takeLatest(DELETE_USER, deleteUserSaga);
  yield takeLatest(BULK_ACTION_USERS, bulkActionUsersSaga);
  yield takeLatest(RESET_USER_PASSWORD, resetPasswordSaga);
  yield takeLatest(TOGGLE_TWO_FACTOR, toggleTwoFactorSaga);
  // User access permissions
  yield takeLatest(GET_USER_ACCESS, getUserAccessSaga);
  yield takeLatest(SAVE_USER_ACCESS, saveUserAccessSaga);
  yield takeLatest(RESTORE_USER_ACCESS, restoreUserAccessSaga);
  yield takeLatest(GET_USER_ACCESS_PERMISSIONS, getUserAccessPermissionsSaga);
}



export const AddUserCategory = async (data, isDepartment = false) => {

  try {
    let url = 'company/createCompany';
    if (isDepartment)
      url = '/department/createDepartment';
    const res = await axiosConfig.post(url, data);

    //customNotification.destroy();
    if (res.status === 200) {
      const responseData = get(res, ['data', 'data'], {});

      customNotification.success(isDepartment ? 'Department added successfully.' : 'Company added successfully.');

      return responseData

    }

    else {

      customNotification.error('Failed to add category');
      return null

    }

  } catch (error) {

   // customNotification.destroy();
    if(error?.data.includes('Validation error')){
      customNotification.warning( 'Could not add ' + (isDepartment ? 'Department' : 'Company'),  isDepartment ? `${data.department_name} already exists` : `${data.company_name} already exists`, '');
    }
    else{
      customNotification.error(error.data);
    }
    return null

  }
}

export const getListUserCategory = async (type = 'department') => {
  try {
    let url;
    if (type === 'department')
      url = `department/getalldepartments`;
    else if (type === 'company')
      url = `/company/getallcompanies`;
    else if (type === 'role')
      url = `/role/getAllAppLevelRoles`;


    const res = await axiosConfig.get(url);
    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    } if (res.status === 200) {
      const data = get(res, ['data', 'data'], {});
      return data?.data || [];
    }
    return [];
  } catch (error) {
    return [];
  }
};

export const CreateUser = async (userData) => {
  try {
    const res = await axiosConfig.post(`/user/createUser`, userData);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    if (res.status === 200) {
      customNotification.success('Account created successfully',userData.sendWelcomeEmail ? 'User credentials have been sent to the registered email.' : '');
      return res.data;
    }
    if (res.status === 502) {
      customNotification.error('Service temporarily unavailable', 'The service is temporarily unavailable. Please try again later.');
      return null;
    }
    return null;
  } catch (error) {
    if (error.data.includes('User account already exists')) {
      customNotification.error('User account already exists', 'please try with different email');
      return null;
    }
    else {
      customNotification.error(error.message);
    }
    return null;
  }
}


export const updateUser = async (userData) => {
  try {
    const res = await axiosConfig.put(`/user/updateUser/${userData.id}`, userData);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    if (res.status === 200) {
      customNotification.success(' Updated successfully', 'The user has been updated successfully.');
      const body = get(res, 'data.data', get(res, 'data', {}));
      const savedExtras = parseUserExtraDetails(
        body?.user_extra_details ??
          body?.userExtraDetails ??
          userData.user_extra_details ??
          userData.userExtraDetails
      );
      return {
        id: userData.id,
        ...body,
        user_extra_details: savedExtras,
      };
    }
    return null;
  } catch (error) {
    customNotification.error('Failed to update user', error.message);
    return null;
  }
}

export const updateBulkUser = async (userData) => {
  try {
    const res = await axiosConfig.put(`/user/bulkUpdateUsers`, userData);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    if (res.status === 200) {
      customNotification.success('Status updated successfully', 'The users status has been updated successfully.');
      return res.data;
    }
    return null;
  } catch (error) {
    customNotification.error('Failed to update user', error.message);
    return null;
  }
}


export const deleteBulkUser = async (userIds, isBulk = false) => {
  try {
    let dataObjet = {
      data: {
        userIds: userIds
      }
    }
    const res = await axiosConfig.post(`/user/deleteUsers`, dataObjet);

    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    if (res.status === 200) {
      if(isBulk){ 
        customNotification.success('Users deleted successfully' , 'The users have been deleted successfully from the system.');
      }
      else{
        customNotification.success('User deleted successfully' , 'The user has been deleted successfully from the system.');
      }
      return userIds;
    }
    return null;
  } catch (error) {
    customNotification.error(isBulk ? 'Failed to delete users' : 'Failed to delete user', error.message);
    return null;
  }
}


export const resetUserPassword = async (user) => {
  try {
    const res = await axiosConfig.put(`/auth/resetUserPassword/${user.id}`, user);
    if (res.status === 403) {
      customNotification.error('User not found', 'The user could not be found.');
      return null;
    } if (res.status === 200) {
      customNotification.success('Password reset successfully', 'The user password has been reset.');
      return true;
    }
    if(res.status === 502){
      customNotification.error('Service temporarily unavailable', 'The service is temporarily unavailable. Please try again later.');
      return null;
    }
    return null;
  } catch (error) {
    customNotification.error('Failed to reset password', error.error);
    return null;
  }
};


export const UpdateSystemSettings = async (id,userData) => {

  try {
    const res = await axiosConfig.put(`/user/updateUser/${id}`, userData);
    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    if (res.status === 200) {
      customNotification.success('System settings updated successfully', 'The system settings have been updated successfully.');
     await setUserAccessPermissions(res.data)
      return res.data;
    }
    return null;
  } catch (error) {
    customNotification.error('Failed to update user', error.message);
    return null;
  }
}

/**
 * GET /getEdgeLogDetails - fetches edge device log file list
 * @param {{
 *   deviceId: string,
 *   deviceType: string,
 *   limit?: number,
 *   offset?: number,
 *   search?: string,
 *   dateFrom?: string,
 *   dateTo?: string,
 *   sortOrder?: 'ASC' | 'DESC',
 * }} payload
 * @returns {Promise<{ fileUrls: Array<{ key: string, url: string, lastModified: string }>, count?: number, totalCount?: number, limit?: number, offset?: number, sortOrder?: string, message?: string } | null>}
 */
export const GetEdgeLogDetails = async (payload) => {
  let { deviceId, deviceType, limit, offset, search, dateFrom, dateTo, sortOrder } = payload || {};
  try {
    const params = { deviceId, deviceType };
    if (limit != null) params.limit = limit;
    if (offset != null) params.offset = offset;
    if (search != null && String(search).trim() !== '') params.search = String(search).trim();
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (sortOrder) params.sortOrder = sortOrder;

    const res = await axiosConfig.get('/user/getEdgeLogDetails', {
      params,
    });
    if (res.status === 403) {
      customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
      return null;
    }
    if (res.status === 200) {
      return res.data.data;
    }
    return null;
  } catch (error) {
    customNotification.error('Failed to get edge log details', error.message);
    return null;
  }
};